import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/brand/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import { TERMS_VERSION } from "../legal/legalContent";
import { capture, EVENTS, setUserProps } from "../analytics/posthog";
import { trackReferralSignup } from "../analytics/firstPromoter";
import { getAttribution } from "../analytics/attribution";
import { detectInAppBrowser } from "../lib/inAppBrowser";
import { InAppBrowserNotice } from "../components/auth/InAppBrowserNotice";

export default function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser } = useAuth();
  const [mode, setMode] = useState(searchParams.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ email: "", password: "", name: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(searchParams.has("google_error") ? "No se pudo completar el acceso con Google. Inténtalo otra vez o entra con tu email." : "");
  const [googleEnabled, setGoogleEnabled] = useState(false);
  useEffect(() => { api.get("/auth/providers").then(({ data }) => setGoogleEnabled(data.google === true)).catch(() => {}); }, []);
  const [forgot, setForgot] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [inApp] = useState(() => detectInAppBrowser());

  const routeAfter = (data) => {
    if (!data.onboarding_complete) navigate("/onboarding", { replace: true });
    else navigate("/app", { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    if (mode === "register" && form.password !== form.confirm) {
      setError("Las contraseñas no coinciden"); setLoading(false); return;
    }
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      if (mode === "register") capture(EVENTS.signupStarted, { signup_method: "email" });
      const payload = mode === "register"
        ? { email: form.email, password: form.password, name: form.name, accept_terms: accepted, terms_version: TERMS_VERSION, attribution: getAttribution() }
        : { email: form.email, password: form.password };
      const { data } = await api.post(path, payload);
      if (mode === "register") {
        setUserProps({ signup_method: "email", workspace_id: data.workspace_id, plan: data.plan });
        capture(EVENTS.signupCompleted, { signup_method: "email" });
        capture(EVENTS.registrationCompleted, { method: "email", signup_method: "email" });
        // FirstPromoter lead (no-op unless fpr.js loaded, i.e. consent granted).
        trackReferralSignup({ email: form.email, uid: data.user_id });
      } else {
        capture(EVENTS.loginCompleted, { method: "email" });
      }
      setUser(data);
      routeAfter(data);
    } catch (err) {
      capture(mode === "register" ? EVENTS.signupFailed : EVENTS.loginFailed,
        { method: "email", status: err?.response?.status || "network" });
      setError(apiErr(err));
    } finally { setLoading(false); }
  };

  const google = async () => {
    setLoading(true); setError("");
    capture(EVENTS.googleAuthClicked, { mode, in_app_browser: inApp || undefined });
    try {
      const { data } = await api.post("/auth/google/start", {
        accept_terms: mode === "register" && accepted, terms_version: TERMS_VERSION, attribution: getAttribution(),
      });
      window.location.assign(data.url);
    } catch (e) { setError(apiErr(e)); setLoading(false); }
  };

  const doForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: form.email });
      toast.success("Si el email existe, te hemos enviado instrucciones de recuperación.");
      setForgot(false);
    } catch { toast.error("No se pudo procesar la solicitud."); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-obsidian grid-bg flex flex-col">
      <div className="p-6">
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 py-2.5 text-slate-400 hover:text-white text-sm">
          <ArrowLeft className="h-4 w-4" /> Volver
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8"><Logo size="lg" /></div>
          <div className="card-arch p-8">
            <div className="halo" />
            {forgot ? (
              <form onSubmit={doForgot} className="relative space-y-5">
                <h1 className="font-head text-2xl font-bold text-white">Recuperar contraseña</h1>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input data-testid="forgot-email-input" type="email" required value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-white/[0.03] border-white/10" placeholder="tu@empresa.com" />
                </div>
                <Button data-testid="forgot-submit-button" disabled={loading} className="w-full bg-amber-500 text-black hover:bg-amber-400 rounded-lg font-semibold">
                  Enviar enlace
                </Button>
                <button type="button" onClick={() => setForgot(false)} className="w-full text-sm text-slate-400 hover:text-white">Volver a iniciar sesión</button>
              </form>
            ) : (
              <>
                <div className="relative flex gap-1 rounded-xl bg-white/[0.03] p-1 mb-6">
                  {["login", "register"].map((m) => (
                    <button key={m} data-testid={`auth-tab-${m}`} onClick={() => { setMode(m); setError(""); }}
                      className={`flex-1 rounded-lg py-3 sm:py-2 text-sm font-medium transition-colors ${mode === m ? "bg-amber-500 text-black" : "text-slate-400 hover:text-white"}`}>
                      {m === "login" ? "Entrar" : "Crear cuenta"}
                    </button>
                  ))}
                </div>
                <form onSubmit={submit} className="relative space-y-4">
                  {mode === "register" && (
                    <div className="space-y-2">
                      <Label>Nombre</Label>
                      <Input data-testid="auth-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="bg-white/[0.03] border-white/10" placeholder="Tu nombre" />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input data-testid="auth-email-input" type="email" required value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="bg-white/[0.03] border-white/10" placeholder="tu@empresa.com" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Contraseña</Label>
                      {mode === "login" && <button type="button" onClick={() => setForgot(true)} className="py-2 text-xs text-amber-400 hover:underline">¿Olvidada?</button>}
                    </div>
                    <Input data-testid="auth-password-input" type="password" required minLength={6} value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className="bg-white/[0.03] border-white/10" placeholder="Mínimo 6 caracteres" />
                  </div>
                  {mode === "register" && (
                    <div className="space-y-2">
                      <Label>Confirmar contraseña</Label>
                      <Input data-testid="auth-confirm-password-input" type="password" required minLength={6} value={form.confirm}
                        onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                        className="bg-white/[0.03] border-white/10" placeholder="Repite la contraseña" />
                    </div>
                  )}
                  {error && <p data-testid="auth-error" className="text-sm text-red-400">{error}</p>}
                  {mode === "register" && (
                    <div className="flex items-start gap-2.5">
                      <Checkbox id="accept-terms" data-testid="auth-accept-terms" checked={accepted}
                        onCheckedChange={(v) => setAccepted(v === true)} className="mt-0.5 border-white/20 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500" />
                      <label htmlFor="accept-terms" className="text-xs text-slate-400 leading-relaxed">
                        Acepto los <a href="/terms" target="_blank" rel="noreferrer" data-testid="auth-terms-link" className="text-amber-400 hover:underline">Términos del servicio</a> y he leído la <a href="/privacy" target="_blank" rel="noreferrer" data-testid="auth-privacy-link" className="text-amber-400 hover:underline">Política de privacidad</a>.
                      </label>
                    </div>
                  )}
                  <Button data-testid="auth-submit-button" disabled={loading || (mode === "register" && !accepted)} className="w-full bg-amber-500 text-black hover:bg-amber-400 rounded-lg font-semibold h-11 disabled:opacity-50">
                    {loading ? "Un momento…" : mode === "login" ? "Entrar" : "Crear cuenta gratis"}
                  </Button>
                </form>
                {googleEnabled && <div className="relative my-5 flex items-center gap-3 text-xs text-slate-500">
                  <div className="h-px flex-1 bg-white/10" /> o <div className="h-px flex-1 bg-white/10" />
                </div>}
                {googleEnabled && inApp && <InAppBrowserNotice app={inApp} />}
                {googleEnabled && <Button data-testid="google-auth-button" onClick={google} variant="outline" disabled={loading || (mode === "register" && !accepted)}
                  className="w-full bg-white/[0.03] border-white/10 hover:bg-white/[0.07] text-white rounded-lg h-11">
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
                  Continuar con Google
                </Button>}
              </>
            )}
          </div>
          <p className="mt-6 text-center text-xs text-slate-500">10 minutos · Sin tarjeta · Diagnóstico gratuito</p>
        </div>
      </div>
    </div>
  );
}
